/* eslint-disable @typescript-eslint/no-require-imports */
import React, { useState, useEffect, useLayoutEffect, useRef } from "react";

import "./landscape.css";
import * as Utils from "../../Utils";
import { getFromIndexedDB } from "../../services/indexedDb";
import longhorizontal from "../../assets/longhorizontal.svg";
import longvertical from "../../assets/longvertical.svg";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import SpaceDashboardIcon from "@mui/icons-material/SpaceDashboard";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow, { tableRowClasses } from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";

let allLayers = [];

export const NewLandscapePrintout = () => {
  const [isLoading, setLoading] = useState(true);
  const [imgArray, setImgArray] = useState([]);
  const [overheadImgArray, setOverheadImgArray] = useState([]);
  const [levels, setLevels] = useState(0);
  const [savedAllLayers, setSavedAllLayers] = useState([]);

  const queryString = window.location.hash ? window.location.hash.split("?")[1] || "" : "";
  const urlParams = new URLSearchParams(queryString);
  const rawData = urlParams.get("data");
  const data = rawData ? decodeURIComponent(rawData) : "";
  const container = data ? data.split(":") : [];
  const storagetype = container[0] || "";
  const containerID = Number(container[1]) || 0;
  const quadid = Number(container[2]) || 0;

  const rootRef = useRef();

  const handleClearStates = (e) => {
    if (e) e.preventDefault();
    allLayers.length = 0;
    window.location.href = "#/finalPackingPage";
  };

  const StyledTableRow = styled(TableRow)(() => ({
    [`&.${tableRowClasses.root}`]: {
      height: "28px",
    },
    "&:last-child td, &:last-child th": {
      border: 0,
    },
  }));

  const StyledTableCell = styled(TableCell)(() => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: "#f5f5f5",
      color: "#000",
      fontWeight: "bold",
      fontSize: 10,
      padding: "4px 6px",
      borderBottom: "1px solid #ccc",
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 10,
      padding: "4px 6px",
      borderBottom: "1px solid #eee",
    },
  }));

  async function addImages() {
    try {
      const imagesArray = await getFromIndexedDB("imagesArray");
      setImgArray(Array.isArray(imagesArray) ? imagesArray : []);

      const overheadImagesArray = await getFromIndexedDB("overheadImagesArray");
      setOverheadImgArray(Array.isArray(overheadImagesArray) ? overheadImagesArray : []);
    } catch (err) {
      console.error("Failed to load images from IndexedDB:", err);
      setImgArray([]);
      setOverheadImgArray([]);
    }
  }

  async function waitForSetContainers() {
    try {
      const max = await getLevels(storagetype, containerID, quadid);
      setLevels(max || 0);

      if (max > 0) {
        for (let x = 1; x <= max; x++) {
          await getLayer(max, storagetype, containerID, quadid, x);
        }
        setSavedAllLayers([...allLayers]);
      }
    } catch (err) {
      console.error("Error fetching levels:", err);
      setLevels(0);
    } finally {
      setLoading(false);
    }
  }

  async function getLayer(max, type, palletid, quadNo, levelNo) {
    try {
      const result = await Utils.returnPlaceBoxesByLayer(
        type,
        palletid,
        quadNo,
        levelNo
      );
      if (result && Array.isArray(result) && result.length !== 0) {
        allLayers[levelNo] = { type, palletid, quadNo, levelNo, result };
      }
    } catch (err) {
      console.error(`Error loading layer ${levelNo}:`, err);
    }
  }

  async function getLevels(type, palletid, quadNo) {
    try {
      const maxLevel = await Utils.returnPlaceBoxesMaxLayer(type, palletid, quadNo);
      return maxLevel || 0;
    } catch (err) {
      console.error("Error getting max levels:", err);
      return 0;
    }
  }

  function rotationImage(width, length) {
    const numericWidth = parseFloat(width) || 0;
    const numericLength = parseFloat(length) || 0;
    if (numericWidth >= numericLength) {
      return (
        <img
          src={longhorizontal}
          className="checkmark"
          alt="horizontal"
          width="16px"
          height="16px"
        />
      );
    } else {
      return (
        <img
          src={longvertical}
          className="checkmark"
          alt="vertical"
          width="16px"
          height="16px"
        />
      );
    }
  }

  function basicTable(levelNo) {
    const layerData = allLayers?.[levelNo];
    const rows = Array.isArray(layerData?.result)
      ? layerData.result.slice().sort((a, b) => (a?.position_no || 0) - (b?.position_no || 0))
      : [];

    return (
      <TableContainer component={Paper} elevation={0} className="table-wrapper">
        <Table aria-label="step table" size="small" sx={{
            // Reduces overall text size for the table
            "& .MuiTableCell-root": {
              fontSize: "0.55rem",
              padding: "6px 8px", // Adjust padding for a tighter fit
            },
          }}>
          <TableBody>
            <TableRow>
              <StyledTableCell align="center">NIIN</StyledTableCell>
              <StyledTableCell align="center" sx={{ width: "20%" }}>
                Kit
              </StyledTableCell>
              <StyledTableCell align="center">Location</StyledTableCell>
              <StyledTableCell align="center">Position</StyledTableCell>
              <StyledTableCell align="center">Shape</StyledTableCell>
              <StyledTableCell align="center">Dir</StyledTableCell>
              <StyledTableCell align="center" sx={{ width: '8%' }}>Shelf</StyledTableCell>
            </TableRow>
            {rows.map((row, idx) => (
              <StyledTableRow key={row?.nsn || row?.position_no || idx}>
                <StyledTableCell align="center">
                  {row?.nsn ? String(row.nsn).slice(-4) : "N/A"}
                </StyledTableCell>
                <StyledTableCell align="center">{row?.ksn ?? ""}</StyledTableCell>
                <StyledTableCell align="center">{row?.location ?? ""}</StyledTableCell>
                <StyledTableCell align="center">#{row?.position_no ?? ""}</StyledTableCell>
                <StyledTableCell align="center">
                  <img
                    className="footer-img-grey"
                    src={require("../../assets/retangularprism.png")}
                    alt="shape"
                  />
                </StyledTableCell>
                <StyledTableCell align="center">
                  {rotationImage(row?.width, row?.length)}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {row?.niin === 9999 && <SpaceDashboardIcon fontSize="small" />}
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  function getDocumentTitle(storagetype) {
    const quads = ["ALPHA", "BRAVO", "CHARLIE", "DELTA"];
    const quadName = quads[quadid - 1] || `QUADRANT 0${quadid}`;

    if (storagetype === "ISU-90") {
      return `ISU ${containerID} QUADRANT ${quadName}`;
    } else {
      return `${storagetype} | Pallet 0${containerID} | Step by Step`;
    }
  }

  const Page = ({ pageNumber, totalPages, levelNo }) => {
    const mainImg = imgArray?.[levelNo - 1];
    const overheadImg = overheadImgArray?.[levelNo - 1];
    const fullPackImg = imgArray?.[imgArray.length - 1];

    return (
      <div className="print-page-landscape">
        <div className="page-content-landscape">
          <div className="header-row">
            <h1 className="doc-title-ls">{getDocumentTitle(storagetype)}</h1>
            <div className="page-number-text">
              PAGE {pageNumber}/{totalPages}
            </div>
          </div>

          <p className="instruction-text-ls">
            1- Find the boxes for each door by identifying last 4 digits of
            NIIN. | 2- For each box, look at the icons (see key at the bottom of
            the page) to guide you in placing them. One icon indicates the
            longest side of the box, helping you orient it correctly. | 3- Put
            each box into the correct position on each layer.
          </p>

          <div className="landscape-grid">
            <div className="grid-left">{basicTable(levelNo)}</div>

            <div className="grid-center">
              <div className="main-image-wrapper">
                {mainImg && (
                  <img
                    className={`main-step-image ${
                      storagetype === "ISU-90"
                        ? "isu-diagram"
                        : "pallet-diagram"
                    }`}
                    src={mainImg}
                    alt={`Step ${levelNo}`}
                  />
                )}
              </div>
              {storagetype === "ISU-90" && (
                <div className="door-label">Door</div>
              )}
            </div>

            <div className="grid-right">
              <div className="sidebar-card">
                <span className="card-label">UPPER VIEW PER LEVEL</span>
                <div className="card-image-box">
                  {overheadImg && (
                    <img
                      className={`card-image ${
                        storagetype === "ISU-90"
                          ? "isu-card-img"
                          : "pallet-card-img"
                      }`}
                      src={overheadImg}
                      alt="Upper view"
                    />
                  )}
                </div>
              </div>

              <div className="sidebar-card">
                <span className="card-label">FULL PACK VIEW</span>
                <div className="card-image-box">
                  {fullPackImg && (
                    <img
                      className={`card-image-1 ${
                        storagetype === "ISU-90"
                          ? "isu-card-img"
                          : "pallet-card-img"
                      }`}
                      src={fullPackImg}
                      alt="Full pack view"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <footer className="page-footer-ls">
          <span className="guide-title">GUIDE:</span>
          <div className="footer-object">
            <span className="bold">#N</span>
            <span className="footer-text">positioning order</span>
          </div>
          <div className="footer-object">
            <img
              className="footer-img-grey"
              src={longhorizontal}
              alt="orientation"
            />
            <span className="footer-text">longest side direction</span>
          </div>
          <div className="footer-object">
            <img
              className="footer-img-grey"
              src={require("../../assets/retangularprism.png")}
              alt="shape"
            />
            <span className="footer-text">box shape</span>
          </div>
          {storagetype === "ISU-90" && (
            <div className="footer-object">
              <SpaceDashboardIcon className="footer-img-grey" />
              <span className="footer-text">overlaps section</span>
            </div>
          )}
        </footer>
      </div>
    );
  };

  useLayoutEffect(() => {
    addImages();
    waitForSetContainers();
  }, []);

  if (isLoading) {
    return <div className="loading-state">Loading Printout...</div>;
  }

  const hasNoBoxes =
    levels === 0 ||
    !Array.isArray(allLayers) ||
    !allLayers.some((layer) => Array.isArray(layer?.result) && layer.result.length > 0);

  if (hasNoBoxes) {
    return (
      <div className="no-boxes-container">
        <Button
          className="backbutton-sticky"
          variant="contained"
          onClick={handleClearStates}
        >
          Back
        </Button>
        <div className="no-boxes-card">
          <h2>No boxes</h2>
        </div>
      </div>
    );
  }

  const totalPages = levels > 0 ? levels : 1;

  return (
    <div className="print-root" ref={rootRef}>
      <Button
        className="backbutton-sticky"
        variant="contained"
        onClick={handleClearStates}
      >
        Back
      </Button>
      {Array.from({ length: totalPages }, (_, index) => (
        <Page
          key={index}
          pageNumber={index + 1}
          totalPages={totalPages}
          levelNo={index + 1}
        />
      ))}
    </div>
  );
};

export default NewLandscapePrintout;